// Persistent phone dock (UX spec 6.2). It is not a route: navigation never unmounts it.
import type { Call } from '@bcio-voice/contracts';
import { type FormEvent, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Delete, MicOff, Phone, PhoneOutgoing, RotateCcw, Settings2, ShieldAlert } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { NumberTag } from '../../ui/NumberTag.tsx';
import { type WorkspaceValue, useWorkspace } from '../../state/workspace.tsx';
import { viewAsPhoneDockCopy } from '../../state/viewAs.ts';
import { isIncomingFor } from '../../call/callStore.ts';
import { ActivationGuard } from '../../call/gating.ts';
import { DIAL_KEYS, DIAL_LETTERS, checkDestination, type DestinationDraft } from '../../call/destination.ts';
import type { ActionName } from '../../call/commands.ts';
import { capability, gate } from '../../lib/capabilities.ts';
import { featureNotice } from '../../lib/userCopy.ts';
import { Diagnostics } from '../../ui/Diagnostics.tsx';
import { formatPhone } from '../../lib/format.ts';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { Link } from '../../lib/router.tsx';
import { ActiveCallCard, DOCK_TRAY_EVENT, IncomingCard, StartingCard, WrapUpCard } from './CallCard.tsx';
import { CallStartOptions, armCallWindowAnswer, callWindowAccountOf, callWindowAutoOpen, callWindowFollows } from './CallWindowDock.tsx';
import { useCrmPageAutoPop } from './ContextCard.tsx';
import { callWindowLink } from '../callWindow/callWindowLaunch.ts';
import { CoachingDockCard } from '../coaching/CoachDock.tsx';
import { isCoachingLeg } from '../coaching/monitor.ts';
import { formatLineHyphen, partyName, readinessTone, stateLabel } from './callView.ts';
import { RingNotifications } from '../../lib/notifications.ts';
import { installedApp } from '../../call/phoneOwnership.ts';
import { callLaunchIntents } from '../../call/launchIntents.ts';
import { useDesktop } from '../../desktop/bridge.ts';
import { isSessionOutboundAdmin, numberWarmingDailyLimit, numberWarmingInlineText, pstnReadiness, resolveOutboundLineSelection } from '../../call/pstn.ts';
import { endedForWrapUp, noteField, wrapUpReducer, wrapUpTransition } from './wrapUp.ts';
import { dropButton, dropDockCalls, dropLineReleased, hasOpenDropAudio, isDropping, localDrops, useLocalDrops } from './voicemailDrop.ts';
import { DropCommandNotice, DroppingCard, useDroppingCards } from './DropControl.tsx';
import { playCallEndBeeps, startIncomingRingtone } from './callEndTone.ts';

function formatOptionLabel(n: { label: string; e164: string }): string {
  const formatted = formatPhone(n.e164);
  const digits = n.e164.replace(/\D/g, '').slice(-10);
  const last7 = digits.slice(-7);
  const labelDigits = n.label.replace(/\D/g, '');
  if (
    (digits.length === 10 && labelDigits.endsWith(digits)) ||
    (last7.length === 7 && labelDigits.endsWith(last7))
  ) {
    const cleaned = n.label
      .replace(/\s*[-\u00b7]?\s*(?:\+?1[-.\s]?)?(?:\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}\s*$/, '')
      .trim();
    return cleaned ? `${cleaned} ${formatted}` : formatted;
  }
  return `${n.label} ${formatted}`;
}

export function PhoneDock({ id = 'phone' }: { id?: string }) {
  const ws = useWorkspace();
  const guard = useMemo(() => new ActivationGuard(500), []);
  const drops = useLocalDrops();
  // A coaching leg (the coach's own view of a coached call) gets its own card: no hold, transfer or keypad.
  const coaching = ws.liveCalls.filter(c => isCoachingLeg(c, ws.userId));
  const incoming = ws.liveCalls.filter(c => !isCoachingLeg(c, ws.userId) && isIncomingFor(c, ws.userId));
  const candidates = dropDockCalls(ws.calls, ws.liveCalls, drops, ws.userId, ws.bridge.calls).filter(c => !ws.locallyEnded?.has(c.id));
  const active = candidates.filter(c => !isCoachingLeg(c, ws.userId) && !isIncomingFor(c, ws.userId) && !isDropping(c, drops.get(c.id), ws.userId, ws.bridge.calls));
  const dropNotices = [...ws.calls.values()].filter(c => (drops.get(c.id)?.phase === 'failed' || drops.get(c.id)?.phase === 'unknown')
    && !active.some(a => a.id === c.id) && (dropLineReleased(c, drops.get(c.id), ws.userId, ws.bridge.calls) || c.state === 'ended' || c.state === 'failed'));
  const dropping = useDroppingCards();
  useCallAnnouncements();
  useCallShortcuts(guard);
  // The call window learns an outbound call's ID once the server confirms it (the Dial click opened it before).
  useEffect(() => { callWindowLink.observe(ws.bridge.calls, ws.callControl.failedStart); }, [ws.bridge.calls, ws.callControl.failedStart]);
  // The "CRM page" toggle for live cards: their ContextCard now renders in the call window, not in the dock. Only the
  // tab that owns the phone pops it, as when the cards rendered in the dock: never a view-as admin (those are someone
  // else's calls) and never another open Sidetone tab (each would open its own copy).
  useCrmPageAutoPop(crmPagePopsHere(ws) ? active : NO_CALLS);
  // Frames keep the slot they first appeared in: an answered call waiting stays below the held call instead of
  // jumping to the top (liveCalls is newest first) and dropping the held call's controls under the pointer.
  const framed = useStableOrder(active);
  const { announce } = useAnnouncer();
  const dockRef = useRef<HTMLElement>(null);
  const wrap = useWrapUp(active, ws.callControl.starting.length > 0);
  const finishWrapUp = (noteUnsaved: boolean) => {
    wrap.dispatch({ type: 'done' });
    announce(noteUnsaved ? 'Wrap-up closed. Your note isn’t saved yet; it’s kept on the call’s page.' : 'Wrap-up closed');
    dockRef.current?.focus();
  };
  // The drop button had focus and its card is gone: keep focus in the dock rather than losing it to the page.
  const showingDrop = dropping.length > 0;
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const lost = !document.activeElement || document.activeElement === document.body;
    if (lost && wrap.dockHasFocus()) dockRef.current?.focus();
  }, [showingDrop, active.length]);

  if (ws.session.view_as) {
    const viewAsNumbers = ws.numbers.value ?? [];
    const viewAsLines = resolveOutboundLineSelection(viewAsNumbers, null, { isAdmin: isSessionOutboundAdmin(ws.session) });
    return (
      <aside ref={dockRef} id={id} className="dock surface-line" aria-label="Phone" tabIndex={-1}>
        <div className="dock-head">
          <h2 className="dock-title">Phone</h2>
          <span className="chip chip-lamp tone-neutral">
            <span className="dot dot-neutral" aria-hidden="true" />
            <span className="visually-hidden">Phone status: </span>
            Read-only
          </span>
        </div>
        <div className="dock-setup view-as-dock-notice" role="note">
          <p className="inline-reason">{viewAsPhoneDockCopy(ws.session.view_as)}</p>
          {viewAsLines.options.length > 0 ? (
            <div className="field">
              <label htmlFor="view-as-dial-from">Calling from</label>
              <select id="view-as-dial-from" value={viewAsLines.effectiveFromId ?? ''} disabled>
                {viewAsLines.options.map(({ number: n, usable, why, quotaLabel }) => (
                  <option key={n.id} value={n.id} disabled={!usable}>
                    {formatOptionLabel(n)} ({usable ? (quotaLabel ?? n.status) : why})
                  </option>
                ))}
              </select>
              {viewAsLines.fromNumber ? (
                <p className="field-hint">
                  Calling as <NumberTag number={viewAsLines.fromNumber} />
                  {formatLineHyphen(viewAsLines.fromNumber.e164) ? <> <span>({formatLineHyphen(viewAsLines.fromNumber.e164)})</span></> : null}
                  {numberWarmingInlineText(viewAsLines.fromNumber) ? <>, <span>{numberWarmingInlineText(viewAsLines.fromNumber)}</span></> : null}
                </p>
              ) : null}
              {viewAsLines.rotatedFromNumber && viewAsLines.fromNumber ? (
                <p className="field-hint" role="status">
                  {viewAsLines.rotatedFromNumber.label} reached its {numberWarmingDailyLimit(viewAsLines.rotatedFromNumber)}-call warming limit today; auto-switched to spare {viewAsLines.fromNumber.label}.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </aside>
    );
  }

  return (
    <aside ref={dockRef} id={id} className="dock surface-line" aria-label="Phone" tabIndex={-1} onFocus={wrap.onDockFocus} onBlur={wrap.onDockBlur}>
      <div className="dock-head">
        <h2 className="dock-title">Phone</h2>
        <ReadinessChip />
      </div>
      {ws.phoneOwnership && ws.phoneOwnership.status !== 'owner' ? <PhoneSetup /> : null}
      {!ws.phoneOwnership || ws.phoneOwnership.status === 'owner' ? <>
      {coaching.map(c => <CoachingDockCard key={c.id} call={c} />)}
      {/* A call waiting rings below the live call's frame, so the live controls never move down. */}
      {active.length === 0 ? incoming.map(c => <IncomingCard key={c.id} call={c} guard={guard} />) : null}
      {active.length === 0 ? ws.callControl.starting.map(c => <StartingCard key={c.key} engineCall={c} />) : null}
      {dropping.filter(card => {
        const call = ws.calls.get(card.callId);
        return call && dropLineReleased(call, drops.get(call.id), ws.userId, ws.bridge.calls);
      }).map(card => <DroppingCard key={card.callId} card={card} />)}
      {framed.map(c => <ActiveCallCard key={c.id} call={c} guard={guard} />)}
      {active.length > 0 ? incoming.map(c => <IncomingCard key={c.id} call={c} guard={guard} />) : null}
      {dropNotices.map(c => <DropCommandNotice key={`drop-notice-${c.id}`} call={c} />)}
      {wrap.state && wrap.call ? <WrapUpCard key={wrap.call.id} call={wrap.call} wrapUp={wrap.state} dispatch={wrap.dispatch} onDone={finishWrapUp} onOpenPage={() => wrap.dispatch({ type: 'done' })} focusOnMount={wrap.focusOnMount} /> : null}
      {active.length === 0 && coaching.length === 0 && ws.callControl.starting.length === 0 ? <Dialer /> : null}
      {/* Idle preferences follow the dialer; incoming and active call controls keep priority. */}
      {active.length === 0 && coaching.length === 0 && ws.callControl.starting.length === 0 && incoming.length === 0 ? <CallStartOptions /> : null}
      </> : null}
    </aside>
  );
}

const NO_CALLS: readonly Call[] = Object.freeze([]);

/** Whether this tab pops the CRM page for its live calls: it owns the phone (or ownership is not in play) and it is not
 * viewing as someone else. */
export function crmPagePopsHere(ws: Pick<WorkspaceValue, 'session' | 'phoneOwnership'>): boolean {
  return !ws.session.view_as && (!ws.phoneOwnership || ws.phoneOwnership.status === 'owner');
}

/** The IDs in the order they first appeared: known ones keep their place, new ones go last, gone ones drop out. Pure. */
export function stableOrder(previous: readonly string[], ids: readonly string[]): string[] {
  const present = new Set(ids);
  const kept = previous.filter(id => present.has(id));
  for (const id of ids) if (!kept.includes(id)) kept.push(id);
  return kept;
}

/** `items` in first-seen order (see stableOrder). The same input gives the same output, so a repeated render is harmless. */
function useStableOrder<T extends { id: string }>(items: readonly T[]): T[] {
  const order = useRef<string[]>([]);
  const ids = stableOrder(order.current, items.map(i => i.id));
  order.current = ids;
  const byId = new Map(items.map(i => [i.id, i] as const));
  return ids.map(id => byId.get(id)).filter((i): i is T => !!i);
}

/**
 * The after-call wrap-up (UX spec 6.2.10): the dock's last ended call stays as a card until Done, the
 * next call, or its countdown. It opens one render after the live card closes, so the live card's note
 * draft has been written to the tab by then and the wrap-up's note field starts from it.
 */
function useWrapUp(active: readonly Call[], starting: boolean) {
  const ws = useWorkspace();
  const [state, dispatch] = useReducer(wrapUpReducer, null);
  const before = useRef<string[]>([]);
  const dockHasFocus = useRef(false);
  const focusOnMount = useRef(false);
  const ids = active.map(c => c.id).join(' ');
  const calls = ws.calls;
  const locallyEnded = ws.locallyEnded;
  useEffect(() => {
    const now = ids ? ids.split(' ') : [];
    const { ended, started } = wrapUpTransition(before.current, now, callId => endedForWrapUp(calls.get(callId), locallyEnded));
    before.current = now;
    if (ended) {
      // The live card that had focus is gone; move focus to the wrap-up instead of losing it.
      const lost = typeof document !== 'undefined' && (!document.activeElement || document.activeElement === document.body);
      focusOnMount.current = dockHasFocus.current && lost;
      dispatch({ type: 'ended', callId: ended });
    }
    if (started) dispatch({ type: 'next_call' });
  }, [ids, calls, locallyEnded]);
  useEffect(() => { if (starting) dispatch({ type: 'next_call' }); }, [starting]);
  return {
    state,
    call: state ? calls.get(state.callId) ?? null : null,
    dispatch,
    focusOnMount: focusOnMount.current,
    dockHasFocus: () => dockHasFocus.current,
    onDockFocus: () => { dockHasFocus.current = true; },
    // A card removed while focused takes its focus with it; that isn't focus leaving the dock.
    onDockBlur: (e: { currentTarget: HTMLElement; target: EventTarget; relatedTarget: EventTarget | null }) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null) && (e.target as Node).isConnected) dockHasFocus.current = false; },
  };
}

function ReadinessChip() {
  const { readiness } = useWorkspace();
  const tone = readinessTone(readiness.state);
  return (
    <span className={`chip chip-lamp tone-${tone}`}>
      <span className={`dot dot-${tone}`} aria-hidden="true" />
      <span className="visually-hidden">Phone status: </span>
      {readiness.label}
    </span>
  );
}

/** What stands between this browser and a working phone, with the one action that helps. */
export function PhoneSetup({ reasonId = 'phone-why' }: { reasonId?: string }) {
  const ws = useWorkspace();
  const r = ws.readiness;
  if (r.state === 'ready' || r.state === 'no_permission') return null;
  const creating = ws.deviceStatus === 'creating' || ws.deviceStatus === 'checking';
  return (
    <div className="dock-setup">
      <p className="inline-reason" id={reasonId}>{r.reason}</p>
      {r.action === 'setup_device' ? (
        <button type="button" className="btn" onClick={() => void ws.setupDevice()} disabled={creating} aria-busy={creating || undefined}>
          <Icon icon={Settings2} /> {ws.deviceStatus === 'creating' ? 'Setting up this browser…' : 'Use this browser as my phone'}
        </button>
      ) : null}
      {r.action === 'allow_microphone' ? (
        <button type="button" className="btn" onClick={() => void ws.requestMic()}>
          <Icon icon={MicOff} /> {ws.mic === 'denied' ? 'Try the microphone again' : 'Allow microphone'}
        </button>
      ) : null}
      {r.action === 'retry' ? <button type="button" className="btn" onClick={ws.phone.retry}><Icon icon={RotateCcw} /> Retry now</button> : null}
      {r.action === 'use_here' ? <>
        {installedApp() ? <button type="button" className="btn" onClick={ws.phone.useHere}>Move idle phone to this app</button> : ws.phoneOwnership?.owner?.kind === 'tab' && !ws.phoneOwnership.owner.busy ? <button type="button" className="btn" onClick={ws.phone.useHere}>Use phone in this window</button> : <p className="field-hint">Open Sidetone from its installed app icon. To use a browser tab instead, close the window that owns the phone.</p>}
        {ws.phoneOwnership?.owner ? <p className="field-hint">Owner: {ws.phoneOwnership.owner.kind === 'app' ? 'Sidetone app' : 'browser tab'}; {ws.phoneOwnership.owner.busy ? 'busy' : ws.phoneOwnership.owner.state}.</p> : null}
        {ws.phoneOwnership?.message ? <p role="status">{ws.phoneOwnership.message}</p> : null}
      </> : null}
      {r.action === 'turn_on' ? <button type="button" className="btn" onClick={ws.phone.turnOn}>Turn phone on</button> : null}
      {ws.deviceError ? <p className="field-error" role="alert">{ws.deviceError.title}{ws.deviceError.requestId ? ` Reference ${ws.deviceError.requestId}.` : ''}</p> : null}
      <Diagnostics diagnostic={r.diagnostic} />
    </div>
  );
}

type Tab = 'internal' | 'pstn';

function Dialer() {
  const ws = useWorkspace();
  const wsRef = useRef(ws);
  wsRef.current = ws;
  const { announce } = useAnnouncer();
  const [tab, setTab] = useState<Tab>(() => (ws.dialRequest?.draft.kind === 'internal' ? 'internal' : 'pstn'));
  const [userId, setUserId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [number, setNumber] = useState('');
  const [fromId, setFromId] = useState<string | null>(null);
  const [contactId, setContactId] = useState<string | null>(null);
  const [fromLink, setFromLink] = useState(false);
  const [touched, setTouched] = useState(false);
  const numberRef = useRef<HTMLInputElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);

  const internalCap = capability(ws.config.value, 'calling.internal');
  const pstnCap = capability(ws.config.value, 'calling.pstn.outbound');
  const internalGate = gate(internalCap);
  // Outside numbers: only while the server reports `calling.pstn.outbound` enabled for this person.
  const pstn = pstnReadiness(pstnCap);
  const [starting, setStarting] = useState(false);
  const submitLock = useRef(false);
  const submittedSessionAttemptRef = useRef<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const isAdmin = isSessionOutboundAdmin(ws.session);
  const resolvedLines = resolveOutboundLineSelection(ws.numbers.value ?? [], fromId, { isAdmin });
  const fromOptions = resolvedLines.options;
  const voiceNumbers = resolvedLines.usableOptions;
  const effectiveFromId = resolvedLines.effectiveFromId;
  // Number tag (UX spec 3.3): the chosen number's swatch and label under the picker.
  const fromNumber = resolvedLines.fromNumber;
  const rotatedFromNumber = resolvedLines.rotatedFromNumber;
  const exhaustedBlockReason = resolvedLines.exhaustedBlockReason;

  // Prefill from contacts/team/deep links; manual links require pressing Call, while authenticated session intents auto-dial once ready.
  useEffect(() => {
    const req = ws.dialRequest;
    if (!req) { if (fromLink) { setNumber(''); setFromId(null); setFromLink(false); } return; }
    if (req.draft.kind === 'internal') {
      setTab('internal');
      setUserId(req.draft.userId);
      setTimeout(() => filterRef.current?.focus(), 0);
    } else {
      setTab('pstn');
      setNumber(req.draft.input);
      setTimeout(() => numberRef.current?.focus(), 0);
    }
    setContactId(req.contactId);
    setFromLink(req.origin === 'link' || req.origin === 'session');
    if (req.draft.kind === 'pstn' && req.draft.fromNumberId) setFromId(req.draft.fromNumberId);
    setTouched(false);
    if ((req.origin === 'link' || req.origin === 'session') && req.expiresAt) {
      const timer = setTimeout(() => { callLaunchIntents().cancel('The call link expired. Enter the number in the phone to call.'); ws.cancelDial?.(); announce('The call link expired.'); }, Math.max(0, req.expiresAt - Date.now()));
      return () => clearTimeout(timer);
    }
  }, [ws.dialRequest]);

  // "Calling from" defaults to the person's assigned personal line (or shared fallback) when server
  // assignment metadata is present; explicit user selection always takes precedence.

  const draft: DestinationDraft = tab === 'internal' ? { kind: 'internal', userId } : { kind: 'pstn', input: number, fromNumberId: effectiveFromId };
  const check = checkDestination(draft, ws.userId);
  const capabilityBlock = tab === 'internal' ? (internalGate.render === 'degraded' ? null : featureNotice('calling', internalGate)) : pstn.ready ? null : pstn.reason;
  const readinessBlock = ws.readiness.state === 'ready' ? null : ws.readiness.reason;
  const warmingBlock = tab === 'pstn' ? exhaustedBlockReason : null;
  const blockReason = capabilityBlock ?? readinessBlock ?? warmingBlock ?? (starting ? 'A call is already being started.' : null);
  const canCall = !blockReason && check.ok && !!ws.device;
  // With outside calling off, the PSTN tab offers nothing to press: no number field, dialpad or Call.
  const offerCall = tab === 'internal' || pstn.ready;

  // Calls start only through the engine, which posts POST /calls and accepts nothing but this call's own leg.
  const placeDial = useCallback(async (sessionMeta?: { attemptId: string; candidateId: number; controllerId?: string }, gesture = false) => {
    if (!canCall || !check.ok || starting) return;
    if (fromLink && ws.dialRequest?.expiresAt && Date.now() >= ws.dialRequest.expiresAt) { ws.cancelDial?.(); return; }
    if (submitLock.current) return;
    if (sessionMeta && submittedSessionAttemptRef.current === sessionMeta.attemptId) return;
    submitLock.current = true;
    if (sessionMeta) submittedSessionAttemptRef.current = sessionMeta.attemptId;
    if (fromLink) { callLaunchIntents().cancel('Call request submitted.'); ws.cancelDial?.(); }
    // Still inside the Dial click (and only there: the calling-session effect passes no gesture), before the first
    // await: the call window opens now or the browser blocks it. It opens on a pending page and learns the call ID
    // once the server confirms the call; the intent above is gone before the browser copies sessionStorage. Without a
    // gesture (a calling session's next dial) a call window that is already open still follows the new call.
    const now = wsRef.current;
    callWindowLink.armDial({
      account: callWindowAccountOf(now),
      knownKeys: [...now.bridge.calls.map(c => c.key), ...(now.callControl.failedStart ? [now.callControl.failedStart.key] : [])],
      open: callWindowAutoOpen(now, gesture),
      follow: callWindowFollows(now),
    });
    setStarting(true);
    setStartError(null);
    announce('Calling…');
    try {
      const error = await ws.callControl.place({
        destination: check.destination,
        ...(check.fromNumberId ? { from_number_id: check.fromNumberId } : {}),
        ...(contactId ? { contact_id: contactId } : {}),
        ...(sessionMeta ? {
          calling_session: {
            attempt_id: sessionMeta.attemptId,
            candidate_id: sessionMeta.candidateId,
            ...(sessionMeta.controllerId ? { controller_id: sessionMeta.controllerId } : {}),
          },
        } : {}),
      });
      if (error) { setStartError(error); callWindowLink.failLaunch(error); }
    } catch {
      setStartError('The call could not be started. Check the phone connection before trying again.');
      callWindowLink.failLaunch('The call could not be started.');
    }
    finally { setStarting(false); submitLock.current = false; }
  }, [canCall, check, starting, fromLink, ws.dialRequest, ws.cancelDial, ws.callControl, contactId, announce]);

  useEffect(() => {
    const req = ws.dialRequest;
    if (!req || req.origin !== 'session' || !req.session || tab !== 'pstn' || req.draft.kind !== 'pstn' || number !== req.draft.input) return;
    if (!canCall || !check.ok || starting) return;
    void placeDial(req.session);
  }, [ws.dialRequest, tab, number, canCall, check, starting, placeDial]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    await placeDial(ws.dialRequest?.origin === 'session' ? ws.dialRequest.session : undefined, true);
  };

  const roster = (ws.roster.value ?? []).filter(u => u.id !== ws.userId && u.active);
  const shown = roster.filter(u => !filter.trim() || u.display_name.toLowerCase().includes(filter.trim().toLowerCase()));
  const agentById = new Map((ws.agents.value ?? []).map(a => [a.user_id, a]));

  return (
    <form className="dialer" onSubmit={e => void submit(e)} aria-label="Start a call" noValidate>
      <PhoneSetup />
      <div role="tablist" aria-label="Call type" className="segmented">
        {(['pstn', 'internal'] as const).map(t => (
          <button key={t} type="button" role="tab" id={`dial-tab-${t}`} aria-selected={tab === t} aria-controls={`dial-panel-${t}`} tabIndex={tab === t ? 0 : -1} className="segment"
            onClick={() => setTab(t)}
            onKeyDown={e => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { const next = t === 'internal' ? 'pstn' : 'internal'; setTab(next); document.getElementById(`dial-tab-${next}`)?.focus(); } }}>
            {t === 'internal' ? 'Teammate' : 'Outside number'}
          </button>
        ))}
      </div>

      {tab === 'internal' ? (
        <div role="tabpanel" id="dial-panel-internal" aria-labelledby="dial-tab-internal" className="dial-panel">
          <p className="field-hint">Internal call. No phone number used.</p>
          {ws.roster.value === null && !ws.roster.loading ? (
            <p className="inline-reason">{ws.roster.error ? ws.roster.error.title : "Your role can't list teammates."}</p>
          ) : (
            <>
              <div className="field">
                <label htmlFor="dial-filter">Find a teammate</label>
                <input ref={filterRef} id="dial-filter" type="search" value={filter} onChange={e => setFilter(e.target.value)} autoComplete="off" />
              </div>
              {ws.roster.loading ? <p className="field-hint" role="status">Loading teammates…</p> : null}
              {!ws.roster.loading && roster.length === 0 ? <p className="inline-reason">No other teammates in this workspace yet.</p> : null}
              {shown.length > 0 ? (
                <fieldset className="pick-list">
                  <legend className="visually-hidden">Teammate to call</legend>
                  {shown.slice(0, 50).map(u => {
                    const agent = agentById.get(u.id);
                    return (
                      <label key={u.id} className={`pick${userId === u.id ? ' is-selected' : ''}`}>
                        <input type="radio" name="dial-user" value={u.id} checked={userId === u.id} onChange={() => setUserId(u.id)} />
                        <span className="pick-name">{u.display_name}</span>
                        <span className="pick-meta">{agent ? <span className={`dot dot-${presenceTone(agent.availability, agent.device_online)}`} aria-hidden="true" /> : null}{agent ? presenceText(agent.availability, agent.device_online) : 'Presence not shared with you'}</span>
                      </label>
                    );
                  })}
                </fieldset>
              ) : null}
              {filter && shown.length === 0 && roster.length > 0 ? <p className="field-hint">No teammate matches “{filter}”.</p> : null}
            </>
          )}
        </div>
      ) : (
        <div role="tabpanel" id="dial-panel-pstn" aria-labelledby="dial-tab-pstn" className="dial-panel">
          {!pstn.ready ? (
            <div className="notice notice-info" id="pstn-why">
              <p>{pstn.reason}{internalGate.render === 'normal' ? ' You can still call teammates.' : ''}</p>
              {fromLink && number ? <p>The number from the link was {formatPhone(number) || number}. It can’t be called from here right now.</p> : null}
              <Diagnostics diagnostic={pstn.diagnostic} />
            </div>
          ) : (
            <>
              <div className="field">
                <label htmlFor="dial-from">Calling from</label>
                {ws.numbers.loading ? (
                  <p className="field-hint" role="status">Loading numbers…</p>
                ) : voiceNumbers.length === 0 ? (
                  <p className="inline-reason" id="dial-from">
                    {ws.numbers.error ? ws.numbers.error.title : exhaustedBlockReason ? exhaustedBlockReason : fromOptions.length > 0 ? 'None of your numbers can make outgoing calls. Ask an admin to assign one.' : 'No numbers yet. Ask an admin to assign a number.'}
                  </p>
                ) : (
                  <select id="dial-from" value={effectiveFromId ?? ''} onChange={e => setFromId(e.target.value || null)} required aria-invalid={touched && !check.ok && check.field === 'from' ? true : undefined}>
                    <option value="">Choose a number</option>
                    {fromOptions.map(({ number: n, usable, why, quotaLabel }) => (
                      <option key={n.id} value={n.id} disabled={!usable}>
                        {formatOptionLabel(n)} ({usable ? (quotaLabel ?? n.status) : why})
                      </option>
                    ))}
                  </select>
                )}
                {fromNumber ? (
                  <p className="field-hint">
                    Calling as <NumberTag number={fromNumber} />
                    {formatLineHyphen(fromNumber.e164) ? <> <span>({formatLineHyphen(fromNumber.e164)})</span></> : null}
                    {numberWarmingInlineText(fromNumber) ? <>, <span>{numberWarmingInlineText(fromNumber)}</span></> : null}
                  </p>
                ) : null}
                {rotatedFromNumber && fromNumber ? (
                  <p className="field-hint" role="status">
                    {rotatedFromNumber.label} reached its {numberWarmingDailyLimit(rotatedFromNumber)}-call warming limit today; auto-switched to spare {fromNumber.label}.
                  </p>
                ) : null}
              </div>
              {fromLink ? <p className="notice notice-info" role="status">This number came from a link. Check the destination <span className="mono">{number}</span> and choose your outgoing line, then press Call.</p> : null}
              <div className="field">
                <label htmlFor="dial-number">Number to call</label>
                <div className="input-with-action">
                  <input ref={numberRef} id="dial-number" inputMode="tel" autoComplete="off" value={number} onChange={e => setNumber(e.target.value)} aria-invalid={!check.ok && (touched || check.tollFree) && check.field === 'number' ? true : undefined} aria-describedby="dial-number-msg" />
                  <button type="button" className="btn btn-icon" onClick={() => setNumber(n => n.slice(0, -1))} aria-label="Delete last digit" disabled={!number}>
                    <Icon icon={Delete} />
                  </button>
                </div>
                <p id="dial-number-msg" className={!check.ok && (check.emergency || check.tollFree) ? 'notice notice-end' : 'field-hint'} role={!check.ok && (check.emergency || check.tollFree) ? 'alert' : undefined}>
                  {!check.ok && (check.emergency || check.tollFree) ? <><Icon icon={ShieldAlert} /> {check.message}</> : touched && !check.ok && check.field === 'number' ? check.message : check.ok && check.destination.kind === 'pstn' ? formatPhone(check.destination.e164) : 'US numbers can be typed as dialed. Use +country code for others.'}
                </p>
              </div>
              <div className="dialpad" role="group" aria-label="Dialpad">
                {DIAL_KEYS.map(k => (
                  <button key={k} type="button" className="dial-key" onClick={() => setNumber(n => (n + k).slice(0, 32))}>
                    <span className="dial-digit">{k}</span>
                    <span className="dial-letters" aria-hidden="true">{DIAL_LETTERS[k] ?? ''}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {startError ? <p className="cmd-status cmd-failed" role="alert">{startError}</p> : null}
      {!startError && ws.callControl.failedStart ? (
        <p className="cmd-status cmd-failed" role="alert">
          {ws.callControl.failedStart.message}
          <button type="button" className="btn btn-small btn-quiet" onClick={ws.callControl.dismissFailedStart}>Dismiss</button>
        </p>
      ) : null}
      {offerCall && blockReason && blockReason !== ws.readiness.reason ? <p className="inline-reason" id="call-why">{blockReason}</p> : null}
      {offerCall && touched && !check.ok && check.field !== 'number' ? <p className="field-error" role="alert">{check.message}</p> : null}
      {offerCall ? (
        <button type="submit" className="btn btn-live btn-call btn-block" aria-disabled={!canCall || undefined} aria-describedby={blockReason ? (blockReason === ws.readiness.reason ? 'phone-why' : 'call-why') : undefined}>
          <Icon icon={tab === 'internal' ? Phone : PhoneOutgoing} /> Call
        </button>
      ) : null}
      {fromLink ? <button type="button" className="btn" onClick={() => { callLaunchIntents().cancel(); ws.cancelDial?.(); setNumber(''); setFromId(null); setFromLink(false); }}>Cancel linked call</button> : null}
      {ws.readiness.state !== 'ready' ? <p className="field-hint"><Link to="/settings">Phone and device settings</Link></p> : null}
    </form>
  );
}

/** Presence as a lamp: lit when they can take a call here, amber when away or without a phone, struck through for do not disturb. */
export function presenceTone(availability: string, deviceOnline: boolean): 'live' | 'warn' | 'end' | 'neutral' {
  if (availability === 'available') return deviceOnline ? 'live' : 'warn';
  if (availability === 'away') return 'warn';
  if (availability === 'do_not_disturb') return 'end';
  return 'neutral';
}

export function presenceText(availability: string, deviceOnline: boolean): string {
  const base = availability === 'available' ? 'Available' : availability === 'away' ? 'Away' : availability === 'do_not_disturb' ? 'Do not disturb' : 'Offline';
  return availability === 'available' && !deviceOnline ? `${base}, no phone connected` : base;
}

/** Polite/assertive announcements for call state changes. No transcript or contact detail beyond the party name. */
function useCallAnnouncements() {
  const ws = useWorkspace();
  const desktop = useDesktop();
  const { announce } = useAnnouncer();
  const previous = useRef(new Map<string, string>());
  const [notifications] = useState(() => new RingNotifications());
  useEffect(() => () => notifications.clear(), [notifications]);
  const hasIncoming = (!ws.phoneOwnership || ws.phoneOwnership.status === 'owner') && ws.liveCalls.some(c => !isCoachingLeg(c, ws.userId) && isIncomingFor(c, ws.userId));
  useEffect(() => {
    if (!hasIncoming) return;
    return startIncomingRingtone(ws.bridge.media.selectedOutput);
  }, [hasIncoming, ws.bridge.media.selectedOutput]);
  useEffect(() => {
    const owner = ws.phoneOwnership?.status === 'owner';
    const sync = () => notifications.sync(!desktop && owner ? ws.liveCalls.filter(c => isIncomingFor(c, ws.userId)).map(c => ({ id: c.id, caller: partyName(c, ws.userId, ws.userName) })) : [], owner);
    sync();
    window.addEventListener('blur', sync);
    document.addEventListener('visibilitychange', sync);
    return () => { window.removeEventListener('blur', sync); document.removeEventListener('visibilitychange', sync); };
  }, [notifications, desktop, ws.phoneOwnership, ws.liveCalls, ws.userId, ws.userName]);
  useEffect(() => {
    const seen = new Map<string, string>();
    for (const call of dropDockCalls(ws.calls, ws.liveCalls, localDrops.getSnapshot(), ws.userId, ws.bridge.calls)) {
      const released = isDropping(call, localDrops.get(call.id), ws.userId, ws.bridge.calls);
      if (released) continue;
      seen.set(call.id, call.state);
      const before = previous.current.get(call.id);
      if (before === call.state) continue;
      const name = partyName(call, ws.userId, ws.userName);
      if (isIncomingFor(call, ws.userId) && before === undefined) {
        announce(`Incoming call from ${name}. Press Alt Shift A to answer.`, 'assertive');
        // The desktop shell sends its own native notification (DesktopHooks).
      } else announce(stateLabel(call, ws.userId, name));
    }
    for (const [id] of previous.current) {
      if (!seen.has(id)) {
        const ended = ws.calls.get(id);
        // A dropped voicemail isn't "Call ended" for this person: the Dropping card says what happened.
        if (ended && !hasOpenDropAudio(id, ws.bridge.calls) && !dropLineReleased(ended, localDrops.get(id), ws.userId, ws.bridge.calls) && ended.state !== 'dropping') {
          playCallEndBeeps(ws.bridge.media.selectedOutput);
          announce(ended.state === 'failed' ? 'Call failed' : 'Call ended', ended.state === 'failed' ? 'assertive' : 'polite');
        }
      }
    }
    previous.current = seen;
  }, [ws.liveCalls, ws.calls, ws.userId, ws.userName, ws.bridge.calls, announce, desktop]);
}

/** Alt+Shift call shortcuts (UX spec 10). Uses event.code; ignores key repeat; respects the activation guard. */
function useCallShortcuts(guard: ActivationGuard) {
  const ws = useWorkspace();
  const { announce } = useAnnouncer();
  const ref = useRef(ws);
  ref.current = ws;
  const say = useRef(announce);
  say.current = announce;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || !e.shiftKey || e.ctrlKey || e.metaKey || e.repeat) return;
      const w = ref.current;
      const incoming = w.liveCalls.find(c => isIncomingFor(c, w.userId));
      // A call whose drop has started is the server's now: no key may end, hold or mute it.
      const active = dropDockCalls(w.calls, w.liveCalls, localDrops.getSnapshot(), w.userId, w.bridge.calls)
        .find(c => !isIncomingFor(c, w.userId) && !isDropping(c, localDrops.get(c.id), w.userId, w.bridge.calls));
      const fire = (control: string, call: (typeof w.liveCalls)[number], action: ActionName) => {
        e.preventDefault();
        if (!call.allowed_actions.includes(action) || !guard.allow(control, Date.now())) return;
        if (action === 'answer') {
          const ec = w.callControl.engineCall(call);
          if (w.readiness.state !== 'ready' || !ec || ec.phase !== 'offered') return;
          // A key press is a user activation: the call window opens from it, as from the Answer button.
          armCallWindowAnswer(w, call.id);
        }
        void w.callControl.act(call, action);
      };
      switch (e.code) {
        case 'KeyA':
          if (incoming) fire(`answer:${incoming.id}`, incoming, 'answer');
          break;
        case 'KeyE':
          if (incoming) fire(`reject:${incoming.id}`, incoming, 'reject');
          else if (active) fire(`hangup:${active.id}`, active, 'hangup');
          break;
        case 'KeyH':
          if (active) fire(`holdtoggle:${active.id}`, active, active.state === 'held' ? 'resume' : 'hold');
          break;
        case 'KeyM': {
          const ec = active ? w.callControl.engineCall(active) : null;
          if (active && ec && ec.microphone !== 'none') {
            e.preventDefault();
            w.callControl.setMuted(active, !ec.muted);
          }
          break;
        }
        case 'KeyD':
          e.preventDefault();
          (document.getElementById('dial-filter') ?? document.getElementById('dial-number'))?.focus();
          break;
        case 'KeyN': {
          // UX spec 6.2.6: the note field of the live call, the wrap-up card, or the open call's page.
          const field = noteField(document, active?.id ?? null);
          if (field) {
            e.preventDefault();
            // The note sits in the tray's call view; the keypad or the transfer form may be showing instead.
            if (active && (field as HTMLElement).offsetParent === null) {
              window.dispatchEvent(new CustomEvent(DOCK_TRAY_EVENT, { detail: { callId: active.id, view: 'call' } }));
              setTimeout(() => field.focus(), 50);
            } else field.focus();
          }
          break;
        }
        case 'KeyV': {
          // Drop the default voicemail: presses the call card's own button, so its rules and guard apply.
          const button = active ? dropButton(document, active.id) : null;
          if (!button) break;
          e.preventDefault();
          if (!(button as HTMLButtonElement).disabled) button.click();
          else say.current(document.getElementById(button.getAttribute('aria-describedby') ?? '')?.textContent?.trim() || 'Voicemail drop isn’t available on this call.');
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [guard]);
}
